import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api';

const BankAccountModal = ({ existing = null, onClose, onSaved }) => {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    label: '',
    company_id: '',
    bank_name: '',
    account_number_last4: '',
    account_type: 'Current',
    balance: 0,
    ...(existing || {})
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, balance: parseFloat(form.balance) || 0 };
      if (existing?.id) {
        await api.put(`/bank-accounts/${existing.id}`, payload);
      } else {
        await api.post('/bank-accounts', payload);
      }
      onSaved();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 fade-up">
      <div data-testid="bank-modal" className="bg-cream border border-border max-w-lg w-full p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— New Account</p>
            <h2 className="font-heading text-2xl font-light">{existing ? 'Edit Bank Account' : 'Add Bank Account'}</h2>
          </div>
          <button data-testid="close-bank-modal" onClick={onClose} className="text-text-secondary hover:text-maroon"><X className="w-5 h-5" strokeWidth={1} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Account Label</label>
            <input data-testid="bank-form-label" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Wolf Media - Operating" className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Company</label>
            <select data-testid="bank-form-company" required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className={inputCls}>
              <option value="">Select company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Bank Name</label>
              <input data-testid="bank-form-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Account Type</label>
              <select data-testid="bank-form-type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className={inputCls}>
                <option value="Current">Current</option>
                <option value="Savings">Savings</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>A/c Last 4 Digits</label>
              <input data-testid="bank-form-last4" maxLength={4} value={form.account_number_last4 || ''} onChange={(e) => setForm({ ...form, account_number_last4: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Opening Balance (₹)</label>
              <input data-testid="bank-form-balance" type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className={inputCls} />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading text-base transition-colors">Cancel</button>
            <button data-testid="save-bank-button" type="submit" disabled={saving} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-base transition-colors disabled:opacity-50">{saving ? 'Saving...' : (existing ? 'Update' : 'Create Account')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAccountModal;
